// Demand → Tenants — the COUNTERPARTY register, greenfield 6 Sep 2026.
//
// Founder: "forget what we have… it doesn't even consider the scale of 3K."
// The 4 Sep cut predated the settled ontology; this one is built on it and on
// the BM-precedent law: the registry grammar and the drawer anatomy are
// IMPORTED from the Assets & onboarding workspace — one grammar, never
// re-implemented — and every list is server-searched, server-faceted and
// server-paginated before the browser sees a row.
//
// What a tenant IS here: a counterparty that may hold any mix of five roles —
// advertiser (demand), premise owner and device owner (supply, the money
// plane's dated record), payout party (commercial terms + statements),
// partner (managed brands). The drawer says each in the current vocabulary:
// premises by lifecycle, devices with procured stock and out-of-service
// units, cabs DERIVED through in-cab premises (decree 6 Sep — never stored),
// the SIM recorded-vs-billed honesty split, and the TWO planes that share
// the word "agreement" (legal acceptances vs commercial terms) kept apart.
//
// Read-only: every write path stays where it lives (Payouts → Agreements,
// billing service, the asset registries).

import React, { useEffect, useRef, useState } from 'react';
import { NavLink, useSearchParams } from 'react-router-dom';
import {
  Search, Info, Building2, Megaphone, Boxes, Landmark, FileCheck, Users, Handshake,
} from 'lucide-react';
import { Loading, ErrorState } from '../../components/ui';
import api from '../../../services/api';
import {
  EntityDrawer, Drawer, RvCard, RvRow, StatusPill, IdLine, EntityLoading, ExportButton, exportCsv,
} from '../fleet/Onboarding';
import s from '../fleet/Onboarding.module.css';

const inr = (v, dp = 0) => (v == null ? '—'
  : `₹${Number(v).toLocaleString('en-IN', { minimumFractionDigits: dp, maximumFractionDigits: dp })}`);
const inrP = (paise, dp = 0) => inr(Number(paise || 0) / 100, dp);
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const monthLabel = (d) => (d ? new Date(`${String(d).slice(0, 10)}T00:00:00`)
  .toLocaleString('en-IN', { month: 'long', year: 'numeric' }) : '—');

const ROLE_LABEL = {
  premise_operator: 'Premise operator',
  device_owner: 'Device owner',
  platform_tech: 'Technical fees',
  platform_margin: 'pDOOH margin',
};

const FACETS = [
  ['advertiser', 'advertisers', (c) => c?.advertisers],
  ['premise_owner', 'premise owners', (c) => c?.premise_owners],
  ['device_owner', 'device owners', (c) => c?.device_owners],
  ['payout', 'payout parties', (c) => c?.payout_parties],
  ['partner', 'partners', (c) => c?.partners],
];

function gateWords(g) {
  if (!g) return 'no day gate';
  const parts = [];
  if (g.min_trips != null) parts.push(`≥${g.min_trips} trips`);
  if (g.min_driving_hours != null) parts.push(`≥${g.min_driving_hours}h driving`);
  if (g.min_presence_hours != null) parts.push(`≥${g.min_presence_hours}h passengers`);
  return parts.length ? `pays only days with ${parts.join(' and ')}` : 'no day gate';
}

const isAdvertiser = (r) => r.has_billing || Number(r.campaigns) > 0;
const isPremiseOwner = (r) => Number(r.premises_owned) > 0 || Number(r.premises_retired) > 0 || Number(r.registry_nodes) > 0;
const isDeviceOwner = (r) => Number(r.devices_owned) > 0;
const isPayoutParty = (r) => Number(r.statements) > 0 || Number(r.own_terms) > 0 || Number(r.payout_due_paise) > 0;

/* ── the drawer: one counterparty across every plane it touches ─────────── */

function TenantDrawer({ id, row, onClose }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let live = true;
    setD(null); setErr(null);
    api.get(`/v2/tenants/${id}`)
      .then((r) => { if (live) setD(r.data); })
      .catch((e) => { if (live) setErr(e?.response?.data?.error || e.message); });
    return () => { live = false; };
  }, [id]);
  if (!d) {
    return (
      <Drawer onClose={onClose} eyebrow="Tenant" title={row?.name || '…'}>
        <EntityLoading err={err} retry={() => { setErr(null); api.get(`/v2/tenants/${id}`).then((r) => setD(r.data)).catch((e) => setErr(e.message)); }} />
      </Drawer>
    );
  }
  const t = d.tenant;
  const sup = d.supply;
  const prem = sup.premises;
  const dev = sup.devices;
  const advertiser = d.billing != null || Number(row?.campaigns) > 0;
  const premiseOwner = Number(prem.owned) > 0 || Number(prem.retired) > 0 || Number(prem.registry_nodes) > 0;
  const deviceOwner = Number(dev.owned) > 0;
  const openOwn = d.agreements.filter((a) => a.effective_to == null);
  const payoutParty = d.statements.length > 0 || openOwn.length > 0;
  const partner = d.managed_brands.length > 0;
  const fleetRoles = d.terms.filter((x) => ['premise_operator', 'device_owner'].includes(x.role));
  const tdsMissing = payoutParty && fleetRoles.some((x) => x.tds_bps == null);
  // WHAT IS DUE IS WHAT WAS INVOICED. The drawer used to sum
  // net_payable_paise, the share ledger's collection-side residue — the
  // founder's "legacy bull" (24 Sep). The server now sums the documents
  // instead; this reads the same field name from the row-level facet so the
  // two surfaces cannot disagree.
  // 'open' is the live state (V360/V361); 'draft' and 'confirmed' ride along
  // for the life of the enum so this reads correctly on either side.
  // The drawer's list row is `row` (it may be absent when opened directly),
  // and it already carries the server-computed facet — so the list and the
  // drawer cannot disagree about what is owed.
  const due = Number(row?.payout_due_paise || 0);
  const uninvoiced = Number(row?.months_uninvoiced || 0);
  const regLink = (tab) => `/v2/fleet/onboarding?tab=${tab}&q=${encodeURIComponent(t.name)}`;

  const sections = [
    { key: 'overview', Icon: Building2, label: 'overview', render: () => (
      <RvCard title="Identity">
        <RvRow label="Tenant">
          {t.name}
          <div><IdLine id={t.id} /></div>
        </RvRow>
        <RvRow label="Owner">{t.owner_email || <small>no owner user</small>}</RvRow>
        <RvRow label="Kind">
          {t.is_personal ? 'personal tenant' : (t.business_type || 'business')}
          {t.is_personal && <small> — shown because it did business; dormant personal signups stay hidden</small>}
        </RvRow>
        {t.gstin && <RvRow label="GSTIN"><span className={s.rvMono}>{t.gstin}</span></RvRow>}
        <RvRow label="Since">{fmtDate(t.created_at)}</RvRow>
      </RvCard>
    ) },
    { key: 'demand', Icon: Megaphone, label: 'advertiser', state: advertiser ? 'ok' : undefined, render: () => (
      advertiser && d.billing ? (
        <RvCard title="Advertiser — Brands Manager"
          footer={<span className={s.hint}>Spend and invoices live in billing; this is the read.</span>}>
          <RvRow label="Billing account">
            <StatusPill tone={d.billing.status === 'active' ? 'grow' : 'warn'}>{d.billing.status}</StatusPill>
            {' '}<small>· {d.billing.billing_mode}</small>
          </RvRow>
          <RvRow label="Settled" hint="Settlement invoices paid or issued — the money the revenue share runs on.">
            <b>{inr(d.billing.settled_spend)}</b> <small>across {d.billing.settled_invoices} invoices</small>
          </RvRow>
          <RvRow label="Balances">
            accrued {inr(d.billing.accrued_spend)} · prepaid {inr(d.billing.prepaid_balance)} · credits {inr(d.billing.credit_balance)}
          </RvRow>
          {d.billing.last_invoice_at && <RvRow label="Last invoice">{fmtDate(d.billing.last_invoice_at)}</RvRow>}
        </RvCard>
      ) : (
        <RvCard title="Advertiser"><RvRow label="Not an advertiser"><small>No billing account and no campaigns.</small></RvRow></RvCard>
      )
    ) },
    { key: 'supply', Icon: Boxes, label: 'supply',
      state: premiseOwner || deviceOwner
        ? (Number(prem.drift) > 0 ? 'drift' : (Number(prem.owned) === 0 && Number(prem.registry_nodes) > 0 ? 'house' : 'ok'))
        : undefined,
      render: () => (
        <>
          <RvCard title="Premises — the money plane's dated record"
            footer={<NavLink className={s.btnTool} to={regLink('deployments')}>Open in Premises registry →</NavLink>}>
            <RvRow label="Owned" hint="Current ownership rows on the premise axis — what earns the premise share.">
              {Number(prem.owned) + Number(prem.retired) > 0 ? (
                <>
                  <b>{Number(prem.owned) + Number(prem.retired)}</b>
                  {' '}<small>
                    {[prem.deployed > 0 && `${prem.deployed} deployed`,
                      prem.onboarding > 0 && `${prem.onboarding} onboarding`,
                      prem.parked > 0 && `${prem.parked} parked — delivery off`,
                      prem.retired > 0 && `${prem.retired} retired`].filter(Boolean).join(' · ')}
                  </small>
                </>
              ) : <small>none owned</small>}
            </RvRow>
            <RvRow label="Registry tags" hint="edge_nodes_devices.tenant_id — the operational label, NOT the money owner. A tagged-but-unowned premise's earnings stay house money.">
              {Number(prem.registry_nodes) > 0
                ? <>{prem.registry_nodes} tagged{Number(prem.owned) === 0 ? <span className={s.unmapped}> — house money until ownership transfers</span> : null}</>
                : <small>none</small>}
              {Number(prem.drift) > 0 && <div><StatusPill tone="bad">{prem.drift} DRIFT — tag disagrees with money owner</StatusPill></div>}
            </RvRow>
            <RvRow label="Cabs (derived)" hint="A vehicle exists because an in-cab premise exists; its owner DERIVES through that premise (decree 6 Sep). Never stored, always computed.">
              {Number(sup.cabs) > 0 ? <><b>{sup.cabs}</b> <small>active vehicle links on owned in-cab premises</small></> : <small>none</small>}
            </RvRow>
          </RvCard>
          <RvCard title="Devices — the device axis"
            footer={<NavLink className={s.btnTool} to={regLink('screens')}>Open in Devices registry →</NavLink>}>
            <RvRow label="Owned" hint="Current ownership rows on the device axis — a shelf device is still owned and earns once it rides.">
              {Number(dev.owned) > 0 ? (
                <>
                  <b>{dev.owned}</b>
                  {' '}<small><span style={{ color: 'var(--grow-text)' }}>{dev.live_now} live now</span>
                    {Number(dev.procured) > 0 ? ` · ${dev.procured} procured — never booted` : ''}</small>
                  {Number(dev.out) > 0 && <> <StatusPill tone="bad">{dev.out} out of service</StatusPill></>}
                </>
              ) : <small>none owned</small>}
            </RvRow>
            <RvRow label="SIMs on their premises" hint="RECORDED plan costs of open allocations. The payout deducts the agreement's configured rate (₹170 per device-month) before TDS — never this sum.">
              {Number(sup.sim.count) > 0
                ? <>{sup.sim.count} allocated <small>· recorded plans {inrP(sup.sim.recorded_month_paise)}/mo — billing uses the agreement rate</small></>
                : <small>none</small>}
            </RvRow>
          </RvCard>
        </>
      ) },
    { key: 'payouts', Icon: Landmark, label: 'payouts', state: payoutParty ? (tdsMissing ? 'miss' : 'ok') : undefined, render: () => (
      <>
        <RvCard title="Commercial terms — which row governs"
          footer={<NavLink className={s.btnTool} to="/v2/payouts/agreements">Agreements &amp; gates →</NavLink>}>
          {d.terms.map((x) => (
            <RvRow key={x.role} label={ROLE_LABEL[x.role] || x.role}>
              {(x.rate_bps / 100).toFixed(x.rate_bps % 100 ? 1 : 0)}% <small>of {x.basis === 'gross' ? 'gross' : 'the pool'}</small>
              {' '}{x.own
                ? <StatusPill tone="iris">own terms · v{x.version}</StatusPill>
                : <StatusPill tone="dim">system default</StatusPill>}
              {['premise_operator', 'device_owner'].includes(x.role) && (
                x.tds_bps == null
                  ? <> <StatusPill tone="warn">TDS not set — blocks confirm</StatusPill></>
                  : <> <small>TDS {(x.tds_bps / 100).toFixed(1)}%</small></>
              )}
              {x.role === 'premise_operator' && (
                <div className={s.rvHint}>{gateWords(x.gate_profile)}
                  {x.expense_config?.sim_per_device_month_paise
                    ? ` · SIM ${inrP(x.expense_config.sim_per_device_month_paise)}/device-month, ungated, pre-TDS` : ''}</div>
              )}
            </RvRow>
          ))}
        </RvCard>
        <RvCard title="Statements"
          footer={<NavLink className={s.btnTool} to="/v2/payouts/statements">Statements →</NavLink>}>
          {d.statements.length === 0
            ? <RvRow label="None yet"><small>No month close has opened a statement against this party.</small></RvRow>
            : <>
              {due > 0 && <RvRow label="Invoiced, unpaid"><b>{inrP(due)}</b></RvRow>}
              {uninvoiced > 0 && (
                <RvRow label="Not yet invoiced">
                  <small>{uninvoiced} month(s) closed with no document — nothing is payable until one is raised.</small>
                </RvRow>
              )}
              {d.statements.slice(0, 6).map((st) => (
                <RvRow key={st.id} label={monthLabel(st.period_month)}>
                  <StatusPill tone={st.status === 'paid' ? 'grow' : 'dim'}>{st.status}</StatusPill>
                  {/* The bold figure is no longer the ledger residue — that is
                      the collection-side working the founder ruled legacy on
                      24 Sep. The ledger stays visible underneath, labelled, so
                      the gate's withholding is still auditable here. */}
                  {' '}<b>{Number(st.invoiced_total_paise) > 0 ? inrP(st.invoiced_total_paise) : '—'}</b>
                  {' '}<small>invoiced</small>
                  <div className={s.rvHint}>
                    share ledger: gross {inrP(st.gross_share_paise)} · gated away {inrP(st.gated_away_paise)} ·
                    SIM −{inrP(st.sim_expense_paise)} (recorded, not charged) · residue {inrP(st.net_payable_paise)}
                    {st.tds_withheld_paise != null && <> · TDS withheld −{inrP(st.tds_withheld_paise)}</>}
                  </div>
                </RvRow>
              ))}
            </>}
        </RvCard>
      </>
    ) },
    ...(partner ? [{ key: 'partner', Icon: Handshake, label: 'partner', state: 'ok', render: () => (
      <RvCard title="Managed brands" >
        {d.managed_brands.map((b) => (
          <RvRow key={b.brand_tenant_id} label={b.brand_name}>
            <small>managed since {fmtDate(b.valid_from)} — draws the staged commission on this brand's book, out of pDOOH's share</small>
          </RvRow>
        ))}
      </RvCard>
    ) }] : []),
    { key: 'legal', Icon: FileCheck, label: 'legal', state: d.acceptances.length ? 'ok' : 'miss', render: () => (
      <RvCard title="Legal — acceptances (moves no rupees)">
        <RvRow label="Two planes" hint="Legal acceptances gate access; COMMERCIAL terms live under payouts. A tenant can hold every acceptance and still run commercial defaults — CabE does today.">
          <small>Acceptance ≠ agreement. The money terms are in the payouts section.</small>
        </RvRow>
        {d.acceptances.length === 0
          ? <RvRow label="None"><span className={s.unmapped}>no TnC accepted on any platform</span></RvRow>
          : d.acceptances.map((a, i) => (
            <RvRow key={i} label={a.agreement}>
              v{a.version} <small>· {fmtDate(a.accepted_at)}{a.accepted_by ? ` · by ${a.accepted_by}` : ''}</small>
            </RvRow>
          ))}
      </RvCard>
    ) },
    { key: 'people', Icon: Users, label: 'people', state: d.members.length ? 'ok' : undefined, render: () => (
      <RvCard title="Members — module grants">
        {d.members.length === 0
          ? <RvRow label="None"><small>No user holds grants on this tenant.</small></RvRow>
          : d.members.map((m) => (
            <RvRow key={m.email} label={m.email}>
              <small>{(m.grants || []).join(' · ')}</small>
            </RvRow>
          ))}
      </RvCard>
    ) },
  ];

  return (
    <EntityDrawer eyebrow="Tenant" title={t.name} onClose={onClose}
      footerNote="Read-only — writes live in Payouts, billing and the asset registries."
      pills={<>
        <StatusPill tone={t.is_active ? 'grow' : 'bad'}>{t.is_active ? 'active' : 'suspended'}</StatusPill>
        {t.is_personal && <StatusPill tone="dim">personal — did business</StatusPill>}
        {advertiser && <StatusPill tone="dim">advertiser</StatusPill>}
        {premiseOwner && <StatusPill tone="dim">premise owner</StatusPill>}
        {deviceOwner && <StatusPill tone="dim">device owner</StatusPill>}
        {payoutParty && <StatusPill tone={tdsMissing ? 'warn' : 'dim'}>payout party{tdsMissing ? ' — TDS not set' : ''}</StatusPill>}
        {partner && <StatusPill tone="dim">partner</StatusPill>}
      </>}
      sections={sections} />
  );
}

/* ── the register ───────────────────────────────────────────────────────── */

const GRID = { gridTemplateColumns: 'minmax(210px, 1.25fr) minmax(150px, .9fr) minmax(210px, 1.25fr) minmax(190px, 1.1fr) 120px' };
const PAGE = 50;

export default function Tenants() {
  const [params, setParams] = useSearchParams();
  const openId = params.get('id');
  const [q, setQ] = useState(() => params.get('q') || '');
  const [role, setRole] = useState('');
  const [data, setData] = useState({ rows: [], counts: null, hidden: 0, offset: 0, loading: true, error: null, lastBatch: 0 });
  const seq = useRef(0);

  const fetchPage = (reset, roleNow = role, qNow = q) => {
    const mySeq = ++seq.current;
    setData((p) => ({ ...p, loading: true, error: null }));
    const offset = reset ? 0 : data.offset + PAGE;
    api.get('/v2/tenants', { params: { limit: PAGE, offset, ...(qNow ? { q: qNow } : {}), ...(roleNow ? { role: roleNow } : {}) } })
      .then((r) => {
        if (mySeq !== seq.current) return;
        setData((p) => ({
          rows: reset ? r.data.tenants : [...p.rows, ...r.data.tenants],
          counts: r.data.counts, hidden: r.data.personal_tenants_hidden,
          offset, loading: false, error: null, lastBatch: r.data.tenants.length,
        }));
      })
      .catch((e) => { if (mySeq === seq.current) setData((p) => ({ ...p, loading: false, error: e })); });
  };
  useEffect(() => {
    const t = setTimeout(() => fetchPage(true), q ? 300 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, role]);

  const rows = data.rows;
  const total = data.counts?.total;
  const openRow = rows.find((r) => r.id === openId);

  return (
    <div className={s.wrap}>
      <header style={{ marginBottom: 'var(--s3)' }}>
        <h1 style={{ font: '700 20px var(--f-display)', letterSpacing: '-.02em', color: 'var(--ink)', margin: 0 }}>Tenants</h1>
        <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--ink-soft)', maxWidth: 780 }}>
          Every counterparty, across every plane it touches — advertiser, premise owner, device owner,
          payout party, partner. Click a row for the full entity; writes live where they always did.
        </p>
      </header>

      <div className={s.searchBar}>
        <Search size={15} aria-hidden="true" style={{ color: 'var(--ink-mute)', flex: 'none' }} />
        <input type="search" value={q} onChange={(e) => setQ(e.target.value)}
               placeholder="Search tenants — name, owner email, GSTIN…" aria-label="Search tenants" />
      </div>

      <div className={s.sheet}>
        <div className={s.toolbar}>
          <ExportButton onClick={() => exportCsv('tenants', [
            ['Tenant', (r) => r.name], ['Personal', (r) => (r.is_personal ? 'yes' : '')],
            ['Owner email', (r) => r.owner_email || ''],
            ['Campaigns', (r) => r.campaigns], ['Settled ₹', (r) => r.settled_spend],
            ['Premises owned', (r) => r.premises_owned], ['Registry tags', (r) => r.registry_nodes],
            ['Drift', (r) => r.drift_nodes], ['Devices owned', (r) => r.devices_owned],
            ['Procured', (r) => r.devices_procured], ['Out of service', (r) => r.devices_out ?? ''],
            ['Own terms rows', (r) => r.own_terms], ['TDS set', (r) => (r.has_tds ? 'yes' : 'no')],
            // THE ONLY PLACE THIS NUMBER LEAVES THE BUILDING. It now carries
            // the invoiced total, not the share ledger's residue, and the
            // header says which — a column headed "Payout due" in a file
            // somebody emails is read as a liability.
            ['Invoiced unpaid ₹', (r) => (Number(r.payout_due_paise) / 100).toFixed(2)],
            ['Months not invoiced', (r) => String(r.months_uninvoiced ?? '')],
            ['Acceptances', (r) => r.acceptances], ['Members', (r) => r.members],
          ], rows, [])} />
          <span className={s.spacer} />
          {['', ...FACETS.map((f) => f[0])].map((key) => {
            const f = FACETS.find((x) => x[0] === key);
            const count = key === '' ? total : f[2](data.counts);
            return (
              <button key={key || 'all'} type="button"
                      className={`${s.chip} ${role === key ? s.chipOn : ''}`}
                      onClick={() => setRole(role === key ? '' : key)}>
                {key === '' ? 'all' : f[1]}{count != null ? ` · ${count}` : ''}
              </button>
            );
          })}
        </div>
        <div className={s.noteBar}>
          A tenant appears here the day it does business — {data.hidden} dormant personal signups stay hidden.
          Supply counts read the money plane's dated ownership; registry tags without ownership stay house money.
        </div>

        <div className={`${s.regRow} ${s.thRow}`} style={GRID}>
          <span className={s.th}>Tenant
            <span className={s.helpDot} title="Business tenants, plus personal tenants that did business (badged)."><Info size={11} /></span>
          </span>
          <span className={s.th}>Advertiser
            <span className={s.helpDot} title="Campaigns and SETTLED spend (settlement invoices paid/issued — what the revenue share runs on)."><Info size={11} /></span>
          </span>
          <span className={s.th}>Supply
            <span className={s.helpDot} title="Money-plane holdings: premises (non-retired) and devices owned, with procured stock, out-of-service units and registry-vs-owner drift called out."><Info size={11} /></span>
          </span>
          <span className={s.th}>Payouts
            <span className={s.helpDot} title="Invoiced and unpaid — the total of the tax invoices raised in this party's name for months not yet settled. It is NOT the share ledger's residue, which is a collection-side figure and larger. Also: whether the party runs its own commercial terms or the system defaults, and their TDS state."><Info size={11} /></span>
          </span>
          <span className={s.th}>Legal
            <span className={s.helpDot} title="TnC/privacy acceptances per platform. Legal plane only — it moves no rupees; commercial terms live under Payouts."><Info size={11} /></span>
          </span>
        </div>

        {data.loading && !rows.length && <div style={{ padding: 'var(--s4)' }}><Loading what="the tenants" /></div>}
        {data.error && <div style={{ padding: 'var(--s4)' }}><ErrorState error={data.error} onRetry={() => fetchPage(true)} /></div>}
        {!data.error && rows.map((r) => (
          <div key={r.id} className={s.regRow} style={GRID} role="button" tabIndex={0}
               onClick={() => setParams((p) => { p.set('id', r.id); return p; }, { replace: false })}
               onKeyDown={(e) => { if (e.key === 'Enter') setParams((p) => { p.set('id', r.id); return p; }); }}>
            <span className={s.regPrimary} style={{ fontFamily: 'inherit' }}>
              {r.name}
              <small>
                {r.is_personal ? 'personal · did business' : (r.business_type || 'business')}
                {r.owner_email ? ` · ${r.owner_email}` : ''}
                {!r.is_active && <span style={{ color: 'var(--bad-text, #b3261e)', fontWeight: 600 }}> · suspended</span>}
              </small>
            </span>
            <span className={s.regMeta}>
              {isAdvertiser(r)
                ? <><b>{r.campaigns}</b> campaigns<small style={{ display: 'block' }}>settled {inr(r.settled_spend)}</small></>
                : <span style={{ color: 'var(--ink-faint)' }}>—</span>}
            </span>
            <span className={s.regMeta}>
              {isPremiseOwner(r) || isDeviceOwner(r) ? (
                <>
                  {Number(r.premises_owned) > 0 || Number(r.devices_owned) > 0
                    ? <b>{r.premises_owned} premises · {r.devices_owned} devices</b>
                    : <span className={s.unmapped}>{r.registry_nodes} tagged — house money</span>}
                  <small style={{ display: 'block' }}>
                    {[Number(r.devices_procured) > 0 && `${r.devices_procured} procured`,
                      Number(r.devices_out) > 0 && `${r.devices_out} out of service`,
                      Number(r.premises_retired) > 0 && `${r.premises_retired} retired`,
                      Number(r.drift_nodes) > 0 && `${r.drift_nodes} DRIFT`].filter(Boolean).join(' · ') || ' '}
                  </small>
                </>
              ) : <span style={{ color: 'var(--ink-faint)' }}>—</span>}
            </span>
            <span className={s.regMeta}>
              {isPayoutParty(r) ? (
                <>
                  <b>{inrP(r.payout_due_paise)}</b> invoiced
                  <small style={{ display: 'block' }}>
                    {Number(r.own_terms) > 0 ? 'own terms' : 'defaults'}
                    {!r.has_tds && <span style={{ color: 'var(--warn-text)', fontWeight: 600 }}> · TDS not set</span>}
                    {` · ${r.statements} statements`}
                  </small>
                </>
              ) : <span style={{ color: 'var(--ink-faint)' }}>—</span>}
            </span>
            <span className={s.regMeta}>
              {Number(r.acceptances) > 0
                ? <>{r.acceptances} accepted</>
                : <span className={s.unmapped}>none</span>}
            </span>
          </div>
        ))}
        {!data.loading && !data.error && !rows.length && (
          <div style={{ padding: 'var(--s4)', color: 'var(--ink-faint)', fontSize: 13 }}>Nothing matches.</div>
        )}
        {data.lastBatch === PAGE && (
          <button type="button" className={s.loadMore} onClick={() => fetchPage(false)} disabled={data.loading}>
            {data.loading ? 'Loading…' : 'Load more'}
          </button>
        )}
        <div className={s.resultsFoot}>
          Results from {total ?? '…'} tenants · {data.hidden} dormant personal hidden
        </div>
      </div>

      {openId && (
        <TenantDrawer id={openId} row={openRow}
          onClose={() => setParams((p) => { p.delete('id'); return p; }, { replace: false })} />
      )}
    </div>
  );
}
